
import React from "react";
import {
  Search,
  SlidersHorizontal,
  X,
  ChevronDown,
} from "lucide-react";

const EnglishQuizFilters = ({
  filters = {},
  onChange,
  onReset,

  subjects = [],

  totalCount = 0,
  filteredCount = 0,

  className = "",
}) => {
  const {
    search = "",
    subject = "",
    status = "",
    attemptType = "",
  } = filters;

  const updateFilter = (name, value) => {
    onChange?.({
      ...filters,
      [name]: value,
    });
  };

  const handleSearchChange = (event) => {
    updateFilter(
      "search",
      event.target.value,
    );
  };

  const hasActiveFilters =
    Boolean(search) ||
    Boolean(subject) ||
    Boolean(status) ||
    Boolean(attemptType);

  const subjectOptions = subjects.map(
    (item) => {
      if (
        typeof item === "string" ||
        typeof item === "number"
      ) {
        return {
          value: String(item),
          label: String(item),
        };
      }

      return {
        value:
          item?._id ||
          item?.id ||
          item?.value ||
          "",
        label:
          item?.name ||
          item?.title ||
          item?.label ||
          item?.name?.name ||
          "Unnamed Subject",
      };
    },
  );

  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}
    >
      {/* ==================================================
          Header
      ================================================== */}

      <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <SlidersHorizontal size={18} />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Quiz Filters
            </h3>

            <p className="text-xs text-gray-500">
              Filter English quizzes
            </p>
          </div>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={() =>
              onReset?.()
            }
            className="inline-flex items-center justify-center gap-1.5 self-start rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 sm:self-auto"
          >
            <X size={14} />
            Clear filters
          </button>
        )}
      </div>

      {/* ==================================================
          Filter Controls
      ================================================== */}

      <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2 xl:grid-cols-4">
        {/* Search */}
        <div className="xl:col-span-1">
          <label
            htmlFor="english-quiz-search"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500"
          >
            Search
          </label>

          <div className="relative">
            <Search
              size={17}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              id="english-quiz-search"
              type="search"
              value={search}
              onChange={
                handleSearchChange
              }
              placeholder="Search quizzes..."
              className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-9 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />

            {search && (
              <button
                type="button"
                onClick={() =>
                  updateFilter(
                    "search",
                    "",
                  )
                }
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Subject */}
        <FilterSelect
          id="english-quiz-subject"
          label="Subject"
          value={subject}
          onChange={(value) =>
            updateFilter(
              "subject",
              value,
            )
          }
        >
          <option value="">
            All subjects
          </option>

          {subjectOptions.map(
            (option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ),
          )}
        </FilterSelect>

        {/* Status */}
        <FilterSelect
          id="english-quiz-status"
          label="Status"
          value={status}
          onChange={(value) =>
            updateFilter(
              "status",
              value,
            )
          }
        >
          <option value="">
            All statuses
          </option>

          <option value="draft">
            Draft
          </option>

          <option value="published">
            Published
          </option>

          <option value="archived">
            Archived
          </option>

          <option value="active">
            Active
          </option>

          <option value="inactive">
            Inactive
          </option>
        </FilterSelect>

        {/* Attempts */}
        <FilterSelect
          id="english-quiz-attempt-type"
          label="Attempts"
          value={attemptType}
          onChange={(value) =>
            updateFilter(
              "attemptType",
              value,
            )
          }
        >
          <option value="">
            All attempt types
          </option>

          <option value="single">
            Single attempt
          </option>

          <option value="multiple">
            Multiple attempts
          </option>
        </FilterSelect>
      </div>

      {/* ==================================================
          Active Filter Summary
      ================================================== */}

      <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50/70 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">
            Filters:
          </span>

          {!hasActiveFilters && (
            <span className="text-xs text-gray-400">
              None applied
            </span>
          )}

          {search && (
            <FilterBadge
              label={`Search: ${search}`}
              onRemove={() =>
                updateFilter(
                  "search",
                  "",
                )
              }
            />
          )}

          {subject && (
            <FilterBadge
              label={`Subject: ${
                subjectOptions.find(
                  (item) =>
                    item.value ===
                    subject,
                )?.label ||
                subject
              }`}
              onRemove={() =>
                updateFilter(
                  "subject",
                  "",
                )
              }
            />
          )}

          {status && (
            <FilterBadge
              label={`Status: ${
                status
                  .charAt(0)
                  .toUpperCase() +
                status.slice(1)
              }`}
              onRemove={() =>
                updateFilter(
                  "status",
                  "",
                )
              }
            />
          )}

          {attemptType && (
            <FilterBadge
              label={`Attempts: ${
                attemptType ===
                "multiple"
                  ? "Multiple"
                  : "Single"
              }`}
              onRemove={() =>
                updateFilter(
                  "attemptType",
                  "",
                )
              }
            />
          )}
        </div>

        <div className="shrink-0 text-xs text-gray-500">
          Showing{" "}
          <span className="font-semibold text-gray-700">
            {filteredCount}
          </span>{" "}
          of{" "}
          <span className="font-semibold text-gray-700">
            {totalCount}
          </span>{" "}
          quizzes
        </div>
      </div>
    </div>
  );
};

// ======================================================
// Select
// ======================================================

const FilterSelect = ({
  id,
  label,
  value,
  onChange,
  children,
}) => (
  <div>
    <label
      htmlFor={id}
      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500"
    >
      {label}
    </label>

    <div className="relative">
      <select
        id={id}
        value={value}
        onChange={(event) =>
          onChange(
            event.target.value,
          )
        }
        className="h-10 w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 pr-9 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      >
        {children}
      </select>

      <ChevronDown
        size={16}
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
      />
    </div>
  </div>
);

// ======================================================
// Filter Badge
// ======================================================

const FilterBadge = ({
  label,
  onRemove,
}) => (
  <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-100">
    <span className="max-w-[220px] truncate">
      {label}
    </span>

    <button
      type="button"
      onClick={onRemove}
      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition hover:bg-blue-100 hover:text-blue-900"
      aria-label={`Remove ${label} filter`}
    >
      <X size={11} />
    </button>
  </span>
);

export default EnglishQuizFilters;

